/**
 * Chat Context Hook
 *
 * Provides context-aware information for the AI chat assistant based on:
 * - Current page/route
 * - User authentication state
 * - Page-specific quick actions and suggestions
 */
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useZustandAuth } from './useZustandAuth';
// Page configurations with context-specific information
const PAGE_CONFIGS = {
    '/': {
        name: 'Home',
        description: 'Main landing page',
        contextHint: 'User is on the home page, may be exploring the site or looking to get started.',
        quickActions: [
            { id: 'browse', label: 'Browse Diamonds', prompt: 'How do I browse the diamond inventory?' },
            { id: 'learn', label: 'Learn About Diamonds', prompt: 'Tell me about the 4Cs of diamond quality' },
            { id: 'contact', label: 'Get in Touch', prompt: 'How can I contact Royal Diamonds?' },
        ],
        suggestions: [
            'What services does Royal Diamonds offer?',
            'How do I create an account?',
            'What makes Royal Diamonds different?',
        ],
    },
    '/dealer': {
        name: 'Dealer Portal',
        description: 'Diamond inventory browsing and search',
        contextHint: 'User is browsing the diamond inventory. They can search, filter, and view detailed diamond specifications.',
        quickActions: [
            { id: 'search', label: 'Search Help', prompt: 'How do I use the search filters to find specific diamonds?' },
            { id: 'compare', label: 'Compare Diamonds', prompt: 'How do I compare multiple diamonds side by side?' },
            { id: 'certs', label: 'View Certificates', prompt: 'How do I view diamond certification details?' },
            { id: 'export', label: 'Export Data', prompt: 'How can I export diamond data to CSV?' },
        ],
        suggestions: [
            'What do the different clarity grades mean?',
            'How do I filter by carat weight?',
            'What certifications do you accept?',
            'Can you explain diamond cut quality?',
        ],
    },
    '/atelier': {
        name: 'Atelier',
        description: 'Diamond inventory and 3D visualization',
        contextHint: 'User is in the Atelier viewing diamonds with advanced visualization features including 3D views and AR.',
        quickActions: [
            { id: 'filter', label: 'Filter Help', prompt: 'How do I use the filters in Atelier?' },
            { id: '3d', label: '3D Viewer', prompt: 'How do I use the 3D diamond viewer?' },
            { id: 'ar', label: 'Try AR', prompt: 'How does the AR (Augmented Reality) feature work?' },
            { id: 'specs', label: 'Understand Specs', prompt: 'Explain the diamond specifications I see here' },
        ],
        suggestions: [
            'What shapes are available?',
            'How accurate are the 3D models?',
            'Can I see how a diamond looks in real life?',
            'What does the cut quality rating mean?',
        ],
    },
    '/memos': {
        name: 'Memos',
        description: 'Diamond memo management',
        contextHint: 'User is managing diamond memos - viewing, creating, or tracking memo requests.',
        quickActions: [
            { id: 'new-memo', label: 'Request Memo', prompt: 'How do I request a new diamond memo?' },
            { id: 'track', label: 'Track Memo', prompt: 'How do I track the status of my memo requests?' },
            { id: 'return', label: 'Return Process', prompt: 'What is the process to return a memo diamond?' },
            { id: 'terms', label: 'Memo Terms', prompt: 'What are the terms and conditions for memos?' },
        ],
        suggestions: [
            'How long can I keep a memo diamond?',
            'What happens if I want to purchase a memo diamond?',
            'Can I request multiple memos at once?',
            'What is your memo policy?',
        ],
    },
    '/learning-center': {
        name: 'Learning Center',
        description: 'Diamond education and resources',
        contextHint: 'User is in the Learning Center exploring educational content about diamonds and jewelry.',
        quickActions: [
            { id: '4cs', label: 'The 4Cs', prompt: 'Explain the 4Cs of diamond quality in detail' },
            { id: 'shapes', label: 'Diamond Shapes', prompt: 'What are the different diamond shapes and their characteristics?' },
            { id: 'care', label: 'Jewelry Care', prompt: 'How should I care for and clean my diamond jewelry?' },
            { id: 'buying', label: 'Buying Guide', prompt: 'What should I look for when buying a diamond?' },
        ],
        suggestions: [
            'What is the best diamond cut?',
            'How are diamonds graded?',
            'What certifications should I look for?',
            'How do I verify a diamond is real?',
        ],
    },
    '/contact': {
        name: 'Contact',
        description: 'Contact information and support',
        contextHint: 'User is on the contact page, likely looking to reach out or get support.',
        quickActions: [
            { id: 'hours', label: 'Business Hours', prompt: 'What are your business hours?' },
            { id: 'location', label: 'Location', prompt: 'Where is Royal Diamonds located?' },
            { id: 'appointment', label: 'Schedule Visit', prompt: 'How can I schedule an appointment?' },
        ],
        suggestions: [
            'How quickly do you respond to inquiries?',
            'Can I visit in person?',
            'Do you offer virtual consultations?',
        ],
    },
    '/login': {
        name: 'Login',
        description: 'User authentication',
        contextHint: 'User is on the login page, may need help with account access.',
        quickActions: [
            { id: 'register', label: 'Create Account', prompt: 'How do I create a new account?' },
            { id: 'forgot', label: 'Forgot Password', prompt: 'I forgot my password, how do I reset it?' },
            { id: 'benefits', label: 'Account Benefits', prompt: 'What are the benefits of having an account?' },
        ],
        suggestions: [
            'Do I need an account to browse?',
            'Is my information secure?',
            'What can I do with a dealer account?',
        ],
    },
    '/profile': {
        name: 'Profile',
        description: 'User account management',
        contextHint: 'User is viewing their profile and account settings.',
        quickActions: [
            { id: 'edit', label: 'Edit Profile', prompt: 'How do I update my profile information?' },
            { id: 'security', label: 'Security', prompt: 'How do I change my password?' },
            { id: 'history', label: 'View History', prompt: 'Where can I see my transaction history?' },
        ],
        suggestions: [
            'Can I update my email address?',
            'How do I change my notification preferences?',
            'Where can I see my saved diamonds?',
        ],
    },
    '/admin/financials': {
        name: 'Financial Hub',
        description: 'Financial management and reporting',
        contextHint: 'User is in the Financial Hub managing suppliers, purchases, and payments.',
        quickActions: [
            { id: 'suppliers', label: 'Manage Suppliers', prompt: 'How do I add or manage suppliers?' },
            { id: 'payments', label: 'Track Payments', prompt: 'How do I track payment status?' },
            { id: 'reports', label: 'Financial Reports', prompt: 'How do I generate financial reports?' },
        ],
        suggestions: [
            'How do I record a new purchase?',
            'Where can I see overdue payments?',
            'How do I export financial data?',
        ],
    },
    '/admin/inventory': {
        name: 'Inventory Hub',
        description: 'Inventory management',
        contextHint: 'User is managing diamond inventory - adding, updating, or importing diamonds.',
        quickActions: [
            { id: 'import', label: 'Import Diamonds', prompt: 'How do I import diamonds from a CSV file?' },
            { id: 'add', label: 'Add Diamond', prompt: 'How do I add a new diamond to inventory?' },
            { id: 'update', label: 'Update Stock', prompt: 'How do I update diamond availability?' },
        ],
        suggestions: [
            'What format should my import file be?',
            'How do I bulk update prices?',
            'Can I import images?',
        ],
    },
    '/admin/appraisal-maker': {
        name: 'Appraisal Maker',
        description: 'Create and manage appraisals',
        contextHint: 'User is creating or managing jewelry appraisals.',
        quickActions: [
            { id: 'create', label: 'New Appraisal', prompt: 'How do I create a new appraisal?' },
            { id: 'template', label: 'Use Template', prompt: 'Are there appraisal templates I can use?' },
            { id: 'export-pdf', label: 'Export PDF', prompt: 'How do I export an appraisal as PDF?' },
        ],
        suggestions: [
            'What information is needed for an appraisal?',
            'How do I add photos to an appraisal?',
            'Can I edit a completed appraisal?',
        ],
    },
};
// Default config for unknown pages
const DEFAULT_CONFIG = {
    name: 'Royal Diamonds',
    description: 'Diamond and jewelry services',
    contextHint: 'User is browsing the Royal Diamonds website.',
    quickActions: [
        { id: 'help', label: 'Get Help', prompt: 'What can you help me with?' },
        { id: 'browse', label: 'Browse Diamonds', prompt: 'How do I browse the diamond inventory?' },
        { id: 'contact', label: 'Contact Us', prompt: 'How can I contact Royal Diamonds?' },
    ],
    suggestions: [
        'What services do you offer?',
        'How do I get started?',
        'Tell me about Royal Diamonds',
    ],
};
/**
 * Hook that provides context-aware information for the chat assistant
 */
export function useChatContext() {
    const location = useLocation();
    const { isAuthenticated, roleName } = useZustandAuth();
    const context = useMemo(() => {
        const path = location.pathname;
        // Find matching config (exact match first, then prefix match for admin routes)
        let config = PAGE_CONFIGS[path];
        if (!config) {
            // Try prefix matching for admin routes
            const adminMatch = Object.keys(PAGE_CONFIGS).find(key => key.startsWith('/admin/') && path.startsWith(key));
            if (adminMatch) {
                config = PAGE_CONFIGS[adminMatch];
            }
        }
        // Fall back to default
        if (!config) {
            config = DEFAULT_CONFIG;
        }
        return {
            path,
            pageName: config.name,
            pageDescription: config.description,
            contextHint: config.contextHint,
            quickActions: config.quickActions,
            suggestions: config.suggestions,
            isAuthenticated,
            userRole: roleName || undefined,
        };
    }, [location.pathname, isAuthenticated, roleName]);
    return context;
}
/**
 * Format page context for inclusion in API request
 */
export function formatContextForAPI(context) {
    const parts = [
        `**CURRENT USER CONTEXT:**`,
        `- Current Page: ${context.pageName} (${context.path})`,
        `- Page Purpose: ${context.pageDescription}`,
        `- Context: ${context.contextHint}`,
    ];
    if (context.isAuthenticated) {
        parts.push(`- User Status: Logged in${context.userRole ? ` as ${context.userRole}` : ''}`);
    }
    else {
        parts.push(`- User Status: Not logged in`);
    }
    parts.push('');
    parts.push('Use this context to provide more relevant and helpful responses.');
    return parts.join('\n');
}
export default useChatContext;
